import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { useLocation } from 'react-router-dom';

import {
  getAnswerDeletionReasonOptions,
  deleteAnswers,
  // updateAnswerDeletionReason,
} from "../services/APIClient.js";

const DeleteAnswerModal = (props) => {
  // Extract props
  const {
    show,
    handleClose,
    taskName,
    studentName,
    assessmentName,
    handleDelete,
    answerIds,
    teacherid,
    isRetakeFullAssessment,
    isClassLevel
  } = props;

  // Ensure isClassLevel is a boolean with a proper default
  const isClassLevelBoolean = isClassLevel === true;

  const location = useLocation();
  const isStudentsResultsPage = location.pathname.includes('/studentsresults') || location.pathname.includes('/student/results');

  console.log("Props:", props);

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deletionReasonText, setDeletionReasonText] = useState("");

  // Store student name in local state
  const [storedStudentName, setStoredStudentName] = useState("");

  // Add logging for component rendering
  console.log("Rendering DeleteAnswerModal with state:", {
    successMessage: !!successMessage,
    showConfirmation,
    show,
    storedStudentName,
    studentName
  });

  useEffect(() => {
    console.log("DeleteAnswerModal mounted with props:", {
      show,
      taskName,
      studentName,
      assessmentName,
      answerIds,
      teacherid,
      isRetakeFullAssessment,
      isClassLevel
    });

    getAnswerDeletionReasonOptions()
      .then(options => {
        console.log("Got deletion reason options:", options);
        setOptions(options);
      })
      .catch(error => {
        console.error("Error getting deletion reason options:", error);
      });
  }, []);

  // Effect to store studentName when it's available
  useEffect(() => {
    // When modal is shown and studentName is available, store it
    if (show && studentName) {
      console.log("Storing student name in local state:", studentName);
      setStoredStudentName(studentName);
    }
  }, [show, studentName]);

  // Track changes to successMessage state
  useEffect(() => {
    console.log("successMessage changed:", {
      hasSuccessMessage: !!successMessage,
      successMessageContent: successMessage ? "Success message content exists" : "No content"
    });
  }, [successMessage]);

  const handleOptionChange = (e) => {
    console.log("Option selected:", e.target.value);
    setSelectedOption(e.target.value);
    if (e.target.value !== "Other") {
      setOtherReason("");
    }
  };

  const handleOtherReasonChange = (e) => {
    console.log("Other reason changed:", e.target.value);
    setOtherReason(e.target.value);
  };

  const handleDeleteClick = () => {
    console.log("Delete button clicked");

    if (!selectedOption) {
      alert("Please select a reason for deletion.");
      return;
    }
    if (
      selectedOption ===
      options.find((option) => option.reason === "Other")?.id &&
      !otherReason
    ) {
      alert("Please provide a reason for deletion.");
      return;
    }

    // Store the deletion reason to display in the confirmation modal
    const reasonText =
      selectedOption === options.find((option) => option.reason === "Other")?.id
        ? otherReason
        : options.find((option) => option.id === selectedOption)?.reason;
    console.log("Setting deletion reason text:", reasonText);
    setDeletionReasonText(reasonText);

    // Show the confirmation modal
    console.log("Showing confirmation modal");
    setShowConfirmation(true);
  };

  // Handle canceling the confirmation
  const handleCancelConfirmation = () => {
    console.log("Canceling confirmation");
    setShowConfirmation(false); // Hide confirmation modal
  };

  // Handle final confirmation of deletion
const onDelete = async () => {
  console.log("Starting deletion process...");
  
  const deletionData = {
    answerIds: answerIds,
    teacherId: teacherid,
    deletionReasonId: selectedOption,
    deletionReason: deletionReasonText,
  };

  console.log("Deletion Data:", deletionData);

  try {
    console.log("Calling deleteAnswers API");
    const deleteSuccess = await deleteAnswers(deletionData);
    console.log("deleteAnswers response:", deleteSuccess);

    if (deleteSuccess) {
      console.log("Deletion successful");

      // Create success message
      const operationType = isRetakeFullAssessment ? "assessment" : "task";
      const rescannableTarget = isClassLevelBoolean 
        ? `students in ${displayStudentName || "the class"}` 
        : displayStudentName || "the student";
      const retestTarget = isRetakeFullAssessment ? assessmentName : taskName;

      setSuccessMessage(
        `${operationType === "assessment" ? "Assessment" : "Task"} successfully reset. Please rescan ${rescannableTarget} to retest ${retestTarget}.`
      );

      // Reset states
      setSelectedOption("");
      setOtherReason("");
      setShowConfirmation(false);

      // Notify parent
      handleDelete(taskName, deletionReasonText, false);

      // Auto-close after 3 seconds
      setTimeout(() => {
        console.log("Auto-closing modal");
        setSuccessMessage("");
        handleClose();

        if (isStudentsResultsPage) {
          window.location.reload();
        }
      }, 3000);
    } else {
      console.error("Deletion failed");
    }
  } catch (error) {
    console.error("Error in deletion process:", error);
  }
};

  // Use the stored student name if available, otherwise fall back to the prop
  const displayStudentName = storedStudentName || studentName;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
    >
      {successMessage ? (
        // Success message view
        <>
          <Modal.Header>
            <Modal.Title>Success!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <span style={{ fontSize: "48px", color: "#28a745" }}>✓</span>
            </div>
            <p className="text-center">{successMessage}</p>
          </Modal.Body>
          <Modal.Footer>
            <button className="gold-action-button" onClick={handleClose}>
              Dismiss
            </button>
          </Modal.Footer>
        </>
      ) : showConfirmation ? (
        // Confirmation view
        <>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Retake</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {isRetakeFullAssessment
                ? `Are you sure you want to retake the following assessment${isClassLevelBoolean ? " for the entire class" : ""
                }?`
                : `Are you sure you want to retake the following task${isClassLevelBoolean ? " for the entire class" : ""
                }?`}
            </p>
            {!isRetakeFullAssessment && (
              <p>
                <strong>Task Name:</strong> {taskName}
              </p>
            )}
            <p>
              <strong>{isClassLevelBoolean ? "Class Name:" : "Student Name:"}</strong>{" "}
              {displayStudentName}
            </p>
            <p>
              <strong>Assessment Name:</strong> {assessmentName}
            </p>
            <p>
              <strong>Retake Reason:</strong> {deletionReasonText}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="white-action-button"
              onClick={handleCancelConfirmation}
            >
              Cancel
            </button>
            <button className="gold-action-button" onClick={onDelete}>
              Confirm Retake
            </button>
          </Modal.Footer>
        </>
      ) : (
        // Initial selection view
        <>
          <Modal.Header closeButton>
            <Modal.Title>
              {isRetakeFullAssessment
                ? "Retake Assessment"
                : "Retake Task"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="modal-info">
              {!isRetakeFullAssessment && (
                <p>
                  <strong>Task Name:</strong> {taskName}
                </p>
              )}
              <p>
                <strong>
                  {isClassLevelBoolean ? "Class Name:" : "Student Name:"}
                </strong>{" "}
                {displayStudentName}
              </p>
              <p>
                <strong>Assessment Name:</strong> {assessmentName}
              </p>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Reason for Retake</strong>
              </Form.Label>
              <Form.Control
                as="select"
                value={selectedOption}
                onChange={handleOptionChange}
              >
                <option value="" disabled>
                  Select a reason...
                </option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.reason}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {selectedOption ===
              options.find((option) => option.reason === "Other")?.id && (
                <Form.Group className="mt-3">
                  <Form.Label>
                    <strong>Other Reason</strong>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={otherReason}
                    onChange={handleOtherReasonChange}
                    className="other-answer-option-width"
                  />
                </Form.Group>
              )}
          </Modal.Body>
          <Modal.Footer>
            <button className="white-action-button" onClick={handleClose}>
              Cancel
            </button>
            <button className="gold-action-button" onClick={handleDeleteClick}>
              Retake {isClassLevelBoolean ? `for class` : ""} {taskName}
            </button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default DeleteAnswerModal;